import {
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { InputField } from "components";
import ActionButton from "components/atoms/ActionButton";
import Button from "components/atoms/Button";
import TableDataComponent from "components/organisms/TableData";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import CrossCheckProv from "services/CrosscheckProv";
import {
  PageMode,
  LastEducationOptions,
  EmployeeStatusOptions,
  WorkspaceOptions,
  YesAndNoOtherOptions,
} from "utils/constant";
import { confirmAlert } from "react-confirm-alert";
import { errorToastNotif, successToastNotif } from "utils/common";

const CrossCheckProvService = new CrossCheckProv();
const TableComponent = ({ handlePageModeChange, handleEditData }) => {
  const [state, setState] = useState({
    page: 1,
    limit: 10,
    searchKeyword: "",
    isLoading: true,
    data: [],
    totalData: 0,
    currentPage: 1,
    nextPage: null,
    previousPage: null,
  });

  const { currentUser } = useSelector((state) => state.authReducer);
  const history = useHistory();

  const getAllCrossCheck = async () => {
    setState((prevState) => ({
      ...prevState,
      isLoading: true,
    }));
    try {
      let search = "";

      if (state.searchKeyword !== "") {
        search = `${state.searchKeyword}`;
      }

      const response = await CrossCheckProvService.getAll({
        page: state.page,
        limit: state.limit,
        search,
      });
      setState((prevState) => ({
        ...prevState,
        data: response.crosscheck,
        limit: response.limit,
        currentPage: response.currentPage,
        totalData: response.total,
        nextPage: response.nextPage,
        previousPage: response.previousPage,
      }));
    } catch (error) {
      console.log(error);
    } finally {
      setState((prevState) => ({
        ...prevState,
        isLoading: false,
      }));
    }
  };

  useEffect(() => {
    getAllCrossCheck();
  }, [state.page, state.searchKeyword]);

  const onPageChange = (page) => {
    setState((prevState) => ({
      ...prevState,
      page: page,
    }));
  };

  const onDataSearch = (e) => {
    const keyword = e.target.value;
    setState({
      ...state,
      searchKeyword: keyword,
    });
  };
  const onRemoveData = (id) => {
    confirmAlert({
      title: "Apakah anda yakin ingin menghapus data?",
      message: "Data akan dihapus secara permanen",
      buttons: [
        {
          label: "Ya",
          onClick: async () => {
            await CrossCheckProvService.remove(id)
              .then((res) => {
                getAllCrossCheck();
                successToastNotif("Data berhasil dihapus");
              })
              .catch((err) => {
                console.log(err);
                errorToastNotif(err);
              });
          },
        },
        {
          label: "Tidak",
        },
      ],
    });
  };

  const columns = [
    {
      header: "Aksi",
      accessorKey: "action",
      cell: ({ row }) => (
        <div className="button-actions">
          <ActionButton
            onClick={() => handleEditData(row.original)}
            btnType={"edit"}
          />
          <ActionButton
            onClick={() => onRemoveData(row.original.kd_id)}
            btnType={"delete"}
          />
        </div>
      ),
    },

    {
      header: "Tgl disimpan",
      accessorKey: "tgl_simpan",
      cell: ({ getValue }) =>
        getValue() ? moment(getValue()).format("YYYY-MM-DD") : null,
    },
    {
      header: "Provinsi",
      accessorKey: "nama_provinsi",
    },
    { header: "Tersedia crosschecker", accessorKey: "sediamikroskopis" },
    {
      header: "Nama crosschecker",
      accessorKey: "namapetugas",
      cell: ({ getValue }) => (getValue() ? getValue() : "-"),
    },
    {
      header: "Kepegawaian",
      accessorKey: "stspegawai",
      cell: ({ getValue }) =>
        getValue()
          ? EmployeeStatusOptions.find((opt) => opt.value === getValue())
              ?.label || null
          : null,
    },
    { header: "NIK/KTP", accessorKey: "nip" },
    {
      header: "Tgl lahir",
      accessorKey: "tlahir",
      cell: ({ getValue }) =>
        getValue() ? moment(getValue()).format("YYYY-MM-DD") : null,
    },
    {
      header: "Pendidikan",
      accessorKey: "pddkan",
      cell: ({ getValue }) =>
        getValue()
          ? LastEducationOptions.find((opt) => opt.value === getValue())
              ?.label || null
          : null,
    },
    {
      header: "Tempat kerja",
      accessorKey: "tmpkerja",
      cell: ({ getValue }) =>
        getValue()
          ? WorkspaceOptions.find((opt) => opt.value === getValue())?.label ||
            null
          : null,
    },
    {
      header: "Tanggal mulai bekerja sebagai crosschecker",
      accessorKey: "tglmulaikerja",
      cell: ({ getValue }) =>
        getValue() ? moment(getValue()).format("YYYY-MM-DD") : null,
    },
    {
      header: "Ketersediaan SK",
      accessorKey: "sk",
      cell: ({ getValue }) =>
        getValue()
          ? YesAndNoOtherOptions.find((opt) => opt.value === getValue())
              ?.label || null
          : null,
    },
    {
      header: "Jumlah pelatihan crosschecker yang diikuti",
      accessorKey: "jumplthan",
    },
    {
      header: "Tanggal ToT mikroskopis malaria terakhir",
      accessorKey: "tgltotmikros",
      cell: ({ getValue }) =>
        getValue() ? moment(getValue()).format("YYYY-MM-DD") : null,
    },
    {
      header: "Hasil ToT mikroskopis malaria terakhir (SENSITIFITAS)",
      accessorKey: "hsltotsensi",
    },
    {
      header: "Hasil ToT mikroskopis malaria terakhir (SPESIFITAS)",
      accessorKey: "hsltotspesi",
    },
    {
      header: "Hasil ToT mikroskopis malaria terakhir (AKURASI SPESIES)",
      accessorKey: "hsltotakurasi",
    },
    {
      header: "Hasil ToT mikroskopis malaria terakhir (HITUNG PARASIT)",
      accessorKey: "hsltotparasit",
    },
    {
      header: "Tanggal pelatihan terakhir",
      accessorKey: "thnplthanakhir",
      cell: ({ getValue }) =>
        getValue() ? moment(getValue()).format("YYYY-MM-DD") : null,
    },
    { header: "Nilai pelatihan", accessorKey: "nilaiplth" },
    {
      header: "Hasil pelatihan terakhir (SENSITIFITAS)",
      accessorKey: "hslplthsensi",
    },
    {
      header: "Hasil pelatihan terakhir (SPESIFISITAS)",
      accessorKey: "hslplthspesi",
    },
    {
      header: "Hasil pelatihan terakhir (AKURASI SPESIES)",
      accessorKey: "hslplthakurasi",
    },
    {
      header: "Hasil pelatihan terakhir (HITUNG PARASIT)",
      accessorKey: "hslplthhtungprst",
    },
    {
      header: "Tanggal uji panel bimtek terakhir",
      accessorKey: "tglbimtekakhir",
      cell: ({ getValue }) =>
        getValue() ? moment(getValue()).format("YYYY-MM-DD") : null,
    },
    { header: "Bimtek dg uji panel", accessorKey: "bimtekpanel" },
    {
      header: "Hasil tes panel dari bimtek terakhir (SENSITIFITAS)",
      accessorKey: "hslbimteksensi",
    },
    {
      header: "Hasil tes panel dari bimtek terakhir (SPESIFISITAS)",
      accessorKey: "hslbimtekspesi",
    },
    {
      header: "Hasil tes panel dari bimtek terakhir (AKURASI SPESIES)",
      accessorKey: "hslbimtekakurasi",
    },
    {
      header: "Hasil tes panel dari bimtek terakhir (HITUNG PARASIT)",
      accessorKey: "hslbimtekhtungprst",
    },
    {
      header: "Tanggal uji profisiensi",
      accessorKey: "tglujiprofis",
      cell: ({ getValue }) =>
        getValue() ? moment(getValue()).format("YYYY-MM-DD") : null,
    },
    {
      header: "Hasil uji profisiensi",
      accessorKey: "hslujiprofis",
    },
    {
      header: "Hasil uji profisiensi terakhir (SENSITIFITAS)",
      accessorKey: "hslprofissensi",
    },
    {
      header: "Hasil uji profisiensi terakhir (SPESIFISITAS)",
      accessorKey: "hslprofisspesi",
    },
    {
      header: "Hasil uji profisiensi terakhir (AKURASI SPESIES)",
      accessorKey: "hslprofisakurasi",
    },
    {
      header: "Hasil uji profisiensi terakhir (HITUNG PARASIT)",
      accessorKey: "hslprofisparasit",
    },
    {
      header: "Tanggal uji kompetensi terakhir",
      accessorKey: "tglujikompakhir",
      cell: ({ getValue }) =>
        getValue() ? moment(getValue()).format("YYYY-MM-DD") : null,
    },
    { header: "Jenis Uji kompetensi", accessorKey: "jenisujikompakhir" },
    { header: "Hasil Uji kompetensi", accessorKey: "hslujikompakhir2" },
  ];

  const tableInstance = useReactTable({
    columns,
    data: state?.data,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      <h1 className="page-title">Crosschecker - Provinsi</h1>
      <div
        className="action-wrapper"
        style={{ marginTop: "10px", marginBottom: "24px" }}
      >
        <div className="action-button">
          {currentUser && currentUser.mode === "Online" && (
            <Button
              variant={"primary"}
              size={"normal"}
              onClick={() => handlePageModeChange(PageMode.add)}
            >
              Tambah Data Crosschecker
            </Button>
          )}
        </div>
        <div className="action-search">
          <InputField
            placeholder={"Cari nama crosschecker"}
            onChange={onDataSearch}
            value={state.searchKeyword}
          />
        </div>
      </div>
      <TableDataComponent
        columnLength={columns?.length}
        tableInstance={tableInstance}
        onPageChange={onPageChange}
        loading={state.isLoading}
        currentPage={state.currentPage}
        limit={state.limit}
        nextPage={state.nextPage}
        previousPage={state.previousPage}
        total={state.totalData}
      />
    </div>
  );
};

export default TableComponent;
